import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import AvatarText from "@/components/reusables/AvatarText";
import { Badge } from "@/components/ui/badge";
import { SOCKET_KEYS } from "@/constants/socket.constant";
import { useSocket } from "@/hooks/useSocket";
import { ExtendedSocketData } from "@/interfaces/workspace.interface";
import { cn, isCustomerPortal } from "@/lib/utils";
import { userDataSlice } from "@/redux/slices/userSlice";
import { RootState } from "@/redux/store";

import logo from "/fav.png";

const WorkspaceAvatarWithXero = (): JSX.Element => {
  const dispatch = useDispatch();
  const workspace = useSelector(
    (state: RootState) => state.user?.workspaceData,
  );

  const customerData = useSelector(
    (state: RootState) => state.user?.customerData,
  );

  const isWorkspaceConnectedWithXero = useSelector(
    (state: RootState) => state.user?.isWorkspaceConnectedWithXero,
  );

  const { socket } = useSocket("/");

  // Effect for handling Xero connection status
  // eslint-disable-next-line
  useEffect(() => {
    if (socket && socket?.connected) {
      const handleXeroConnectionStatus = (data: ExtendedSocketData): void => {
        dispatch(
          userDataSlice.actions.setWorkspaceSyncedWithXero(
            data?.isSyncedWithXero,
          ),
        );
      };

      socket.on(SOCKET_KEYS.XERO_CONNECTION_STATUS, handleXeroConnectionStatus);

      return (): void => {
        socket.off(
          SOCKET_KEYS.XERO_CONNECTION_STATUS,
          handleXeroConnectionStatus,
        );
      };
    }
  }, [socket]);

  return (
    <AvatarText
      src={
        workspace?.logo ??
        (customerData?.logo && customerData.logo !== "null"
          ? customerData.logo
          : logo)
      }
      name={workspace?.name ?? customerData?.name ?? "Work PlanX"}
      title={workspace?.name ?? customerData?.name ?? "Work PlanX"}
      classNames={{
        titleClassName: "max-w-[140px] truncate ",
      }}
      custom={{
        position: "subtitle",
        node: !isCustomerPortal ? (
          <div
            className={cn(
              "flex items-center space-x-2",
              !workspace?.isSyncedWithXero && "space-x-0",
            )}
          >
            <p className='text-xs truncate text-success max-w-[110px]'>
              {workspace?.tenantName}
            </p>
            {!workspace?.isSyncedWithXero ? (
              <Badge
                variant='lightDestructive'
                className={cn("rounded-full py-0 text-xs ")}
              >
                Not Connected
              </Badge>
            ) : (
              <Badge
                variant={
                  isWorkspaceConnectedWithXero
                    ? "lightSuccess"
                    : "lightDestructive"
                }
                className={cn("rounded-full py-0 text-xs")}
              >
                {isWorkspaceConnectedWithXero ? "Live" : "Offline"}
              </Badge>
            )}
          </div>
        ) : (
          <Badge
            variant={
              customerData?.isSyncedWithXero
                ? "lightSuccess"
                : "lightDestructive"
            }
            className={cn("rounded-full py-0 text-xs mt-1")}
          >
            {customerData?.isSyncedWithXero ? "Synced" : "Not Synced"}
          </Badge>
        ),
      }}
    />
  );
};

export default WorkspaceAvatarWithXero;
